import { useState } from "react";
import {
    Breadcrumb,
    Card,
    Tooltip,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { NavLink, useParams } from "react-router-dom";
import styled from "styled-components";

import usersApi, { useUserById } from "../../api/users";
import { Loader, toast } from "../../common/components/core";
import ToggleInput from "../../common/components/core/ToggleInput";
import { useGeneralErrorHandlerCreator } from "../../common/hooks/useGeneralErrorHandler";
import { currentRolesSelector, currentUserSelector } from "../../redux/slices/auth/selectors";
import { UserRole } from "../../types/userCurrent";
import { UserStatus, UserReviewStatus } from "../../types/users";
import { PageTitle } from "../../types/pageTitle";
import { composeErrorHandlers, GenericError } from "../../utils/errorHandling";
import {
    isNodeDeveloper,
    isUserAdmin,
    isUserSuperAdmin,
    parseRoleToText,
    UserRoleStripPrefix,
} from "../../utils/role";
import { parseUserProfileTypeToText } from "../../utils/userProfile";

import UserStatusBox, { UserStatusText } from "./components/UserStatusBox";
import AllowedFunction from "./components/AllowedFunction";
import useUpdateEmailNotificationHandler, {
    UpdateEmailNotificationAction,
} from "./hooks/useUpdateEmailNotificationHandler";
import { UserDetailTooltip } from "./components/UserDetailTooltip";

const UserInformationView = (): JSX.Element => {
    document.title = PageTitle.UserInformationView;
    const { userId } = useParams<{ userId: string }>();
    const onGeneralError = useGeneralErrorHandlerCreator();
    const [resendingValidation, isResendingValidation] =
        useState<boolean>(false);

    const currentRoles = useSelector(currentRolesSelector);

    const {
        data: userData,
        isValidating: userIsValidating,
        mutate: mutateUserById,
    } = useUserById(userId);

    const { handler: updateEmailNotification } =
        useUpdateEmailNotificationHandler(mutateUserById);

    const resendActivation = async () => {
        isResendingValidation(true);
        try {
            await usersApi.resendActivation(userId);
            mutateUserById();
            toast("Activation Link Sent");
        } catch (e) {
            composeErrorHandlers(onGeneralError)(e as GenericError);
        }
        isResendingValidation(false);
    };

    const isCurrentAdmin = () => isUserAdmin(currentRoles);
    const isTargetSuperAdmin = () =>
        UserRoleStripPrefix(UserRole.SuperAdmin) === userData?.role;
    const currentUser = useSelector(currentUserSelector);

    return (
        <Loader isLoading={userIsValidating || resendingValidation}>
            {userData && (
                <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <Breadcrumb className="mt-3">
                            <Breadcrumb.Item linkAs="span">
                                <NavLink to="/user-management">
                                    User Management
                                </NavLink>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active className="fw-bold">
                                User Information
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        <div>
                            <AllowedFunction
                                currentRoles={currentRoles}
                                currentUser={currentUser}
                                userData={userData}
                            ></AllowedFunction>
                        </div>
                    </div>

                    <Card className="mb-2 border-0">
                        <CardHeaderDivStyled>
                            <h2>
                                <UserDetailTooltip
                                    oldData={`${userData.firstName} ${userData.lastName}`}
                                    newData={userData.tmpUser && `${userData.tmpUser?.firstName} ${userData.tmpUser?.lastName}`}
                                ></UserDetailTooltip>
                            </h2>
                            <div>
                                {(userData.status === UserStatus.PENDING_ACTIVATION || userData.status === UserStatus.PENDING) &&
                                    !(
                                        isCurrentAdmin() && isTargetSuperAdmin()
                                    ) && (
                                        <StyledResendButton
                                            tabIndex={0}
                                            className="fs-5"
                                            onClick={resendActivation}
                                            onKeyUp={(e) =>
                                                {(e.keyCode === 13 || e.keyCode === 32) && resendActivation()}
                                            }
                                        >
                                            Resend Activation Link
                                        </StyledResendButton>
                                    )}
                                <UserStatusBox status={userData.status} />
                            </div>
                        </CardHeaderDivStyled>

                        <hr className="bg-border-grey my-3" />

                        <div className="mb-2">
                            <h3>Email Address</h3>
                            <p className="fw-bold">{userData.email}</p>
                        </div>

                        <div className="mb-2">
                            <h3>Profile Type</h3>
                            <p className="fw-bold">
                                {parseUserProfileTypeToText(
                                    userData.profileType as any,
                                )}
                            </p>
                        </div>

                        <div className="mb-2">
                            <h3>Profile Name</h3>
                            <p className="fw-bold">{userData.profileName}</p>
                        </div>

                        <div>
                            <h3>Role Type</h3>
                            <p className="fw-bold">
                                <UserDetailTooltip
                                    className="fw-bold"
                                    oldData={parseRoleToText(userData.role)}
                                    newData={userData.tmpUser && parseRoleToText(userData.tmpUser.role)}
                                ></UserDetailTooltip>
                            </p>
                        </div>
                        {isUserSuperAdmin(currentRoles) &&
                            <div>
                                <h3>Status</h3>
                                <p className="fw-bold">
                                    {UserStatusText(userData.status)}
                                </p>
                                {userData.submitBy && (
                                    <>
                                        <h3>Submitted by</h3>
                                        <p className="fw-bold">
                                            {userData.submitBy}
                                        </p>
                                    </>
                                )}
                                {userData.reviewBy &&
                                    <div className="mb-2">
                                        <h3>
                                            {userData.reviewStatus === UserReviewStatus.APPROVED ?
                                            "Approved By" :
                                            "Rejected By"}
                                        </h3>
                                        <p className="fw-bold">{userData.reviewBy}</p>
                                    </div>
                                }
                                {userData.rejectReason &&
                                    <div className="mb-2">
                                        <h3>Rejected Reason</h3>
                                        <p className="fw-bold">{userData.rejectReason}</p>
                                    </div>
                                }
                            </div>
                        }
                    </Card>

                    {isNodeDeveloper() &&
                        isUserSuperAdmin(currentRoles) &&
                        userData.role ===
                            UserRoleStripPrefix(UserRole.HSBCOperator) && (
                            <Card className="mb-2 border-0">
                                <CardHeaderDivStyled>
                                    <h2>HSBC Operator Rights</h2>
                                </CardHeaderDivStyled>

                                <hr className="bg-border-grey my-3" />

                                <div className="">
                                    <h3>
                                        Email Notifications
                                    </h3>
                                    <ToggleInput
                                        checked={userData.isEnableInvEmailNotif}
                                        onChange={() => {
                                            updateEmailNotification(
                                                userData.isEnableInvEmailNotif
                                                    ? UpdateEmailNotificationAction.DISABLE
                                                    : UpdateEmailNotificationAction.ENABLE,
                                                `${userData.firstName} ${userData.lastName}`,
                                                userData.id,
                                            );
                                        }}
                                    />
                                </div>
                            </Card>
                        )}
                </>
            )}
        </Loader>
    );
};

const CardHeaderDivStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const StyledToolTip = styled(Tooltip)`
    .tooltip-arrow {
        display: none;
    }
    .tooltip-inner {
        background-color: rgba(163, 163, 163, 0.8);
        max-width: 360px;
        padding: 12px;
        text-align: left;
    }
`;

const StyledResendButton = styled.a`
    cursor: pointer;
    margin: 0 24px 0 0;
    display: inline-block;
    color: #3C31DD;
`;

export default UserInformationView;
